<template><div></div></template>

<script setup lang="ts">
const ANCHOR_PATTERN = /(fn|footnote|note)[:\-_\d]/gi;
const PRINT_HIDDEN = "footnote--print-hidden";

const BUTTON_TEMPLATE = `<button class="footnote-button" aria-expanded="false">
  <svg role="img" viewBox="0 0 31 6" preserveAspectRatio="xMidYMid">
    <circle r="3" cx="3" cy="3" fill="white"></circle>
    <circle r="3" cx="15" cy="3" fill="white"></circle>
    <circle r="3" cx="27" cy="3" fill="white"></circle>
  </svg>
</button>`;

const POPOVER_TEMPLATE = `<aside class="footnote-popover">
  <div class="footnote-popover__wrapper">
    <div class="footnote-popover__content"></div>
  </div>
  <div class="footnote-popover__tooltip"></div>
</aside>`;

interface Footnote {
  id: string;
  button: HTMLButtonElement;
  popover: HTMLElement;
  content: HTMLElement;
  wrapper: HTMLElement;
  host: HTMLElement;
}

function getHorizontalCenter(el: HTMLElement): number {
  const marginLeft = parseFloat(getComputedStyle(el).marginLeft) || 0;
  const width = el.offsetWidth - marginLeft;
  return (el.getBoundingClientRect().left + width / 2) / window.innerWidth;
}

function getMaxHeight(el: HTMLElement): number {
  const value = parseFloat(getComputedStyle(el).maxHeight);
  return Number.isNaN(value) ? Infinity : Math.round(value);
}

// biome-ignore lint: vendored utility
function throttle<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  let lastCall = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  // biome-ignore lint: vendored utility
  return function (this: any, ...args: any[]) {
    const now = Date.now();
    const remaining = Math.max(0, lastCall + delay - now);

    if (remaining === 0) {
      lastCall = now;
      fn.apply(this, args);
      return;
    }

    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      lastCall = Date.now();
      timer = undefined;
      fn.apply(this, args);
    }, remaining);
  } as T;
}

function findFootnoteLinks(): HTMLAnchorElement[] {
  return Array.from(
    document.querySelectorAll<HTMLAnchorElement>('a[href*="#"]')
  ).filter((a) => (a.href + a.rel).match(ANCHOR_PATTERN));
}

function extractContent(sourceEl: HTMLElement, anchorId: string): string {
  const clone = document.createElement("div");
  clone.innerHTML = sourceEl.innerHTML;

  // Remove back-references to the anchor
  clone.querySelectorAll(`[href$="#${anchorId}"]`).forEach((el) => {
    let node: HTMLElement | null = el as HTMLElement;
    const parent = node.parentElement;
    node.remove();
    // Clean up empty parent elements
    node = parent;
    while (
      node &&
      node !== clone &&
      !node.innerHTML.replace(/(\[\]|&nbsp;|\s)/g, "")
    ) {
      const next = node.parentElement;
      node.remove();
      node = next;
    }
  });

  const html = clone.innerHTML.trim();
  return html.startsWith("<") ? html : `<p>${html}</p>`;
}

function hideForPrint(...elements: HTMLElement[]) {
  for (const el of elements) el.classList.add(PRINT_HIDDEN);
}

function hideEmptyAncestors(el: HTMLElement) {
  const parent = el.parentElement;
  if (!parent) return;
  const children = Array.from(
    parent.querySelectorAll(`:scope > :not(.${PRINT_HIDDEN})`)
  );
  const allHr = children.filter((c) => c.tagName === "HR");
  if (children.length === allHr.length) {
    hideForPrint(...(allHr as HTMLElement[]), parent);
    hideEmptyAncestors(parent);
  }
}

function createFootnote(
  anchor: HTMLAnchorElement,
  index: number
): Footnote | null {
  const targetId = anchor.href.split("#")[1];
  if (!targetId) return null;

  const targetEl = document.getElementById(targetId)?.closest("li");
  if (!targetEl) return null;

  const referenceEl = anchor.closest("sup") || anchor;
  const anchorId = referenceEl.id || anchor.id;
  const id = String(index + 1);

  const content = extractContent(targetEl, anchorId);

  // Hide originals
  hideForPrint(referenceEl as HTMLElement, targetEl);
  hideEmptyAncestors(targetEl);

  // Create button
  const host = document.createElement("span");
  host.classList.add("footnote-host");
  host.innerHTML = BUTTON_TEMPLATE;
  const button = host.querySelector("button")!;
  button.title = `See Footnote ${id}`;
  button.querySelector("svg title")?.remove();

  const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
  title.textContent = `Footnote ${id}`;
  button.querySelector("svg")!.prepend(title);

  referenceEl.insertAdjacentElement("beforebegin", host);

  // Create popover
  const popoverContainer = document.createElement("div");
  popoverContainer.innerHTML = POPOVER_TEMPLATE;
  const popover = popoverContainer.firstElementChild as HTMLElement;
  const wrapper = popover.querySelector(
    ".footnote-popover__wrapper"
  ) as HTMLElement;
  const contentEl = popover.querySelector(
    ".footnote-popover__content"
  ) as HTMLElement;
  contentEl.innerHTML = content;

  return { id, button, popover, content: contentEl, wrapper, host };
}

function positionPopover(footnote: Footnote, currentPosition: string): string {
  const { button, popover, content, wrapper } = footnote;
  const maxHeight = getMaxHeight(content);

  // Determine if popover should go above or below
  const buttonRect = button.getBoundingClientRect();
  const buttonCenter = buttonRect.top + buttonRect.height / 2;
  const marginTop = parseFloat(getComputedStyle(popover).marginTop) || 0;
  const popoverHeight = 2 * marginTop + popover.offsetHeight;
  const spaceBelow = window.innerHeight - buttonCenter;
  const position =
    spaceBelow >= popoverHeight || spaceBelow >= buttonCenter
      ? "below"
      : "above";

  if (position !== currentPosition) {
    popover.classList.remove(`is-${currentPosition}`);
    popover.classList.add(`is-${position}`);
    const originX = `${100 * getHorizontalCenter(button)}%`;
    const originY = position === "above" ? "100%" : "0";
    popover.style.transformOrigin = `${originX} ${originY}`;
  }

  const availableHeight =
    position === "below"
      ? spaceBelow - marginTop - 15
      : buttonCenter - marginTop - 15;
  content.style.maxHeight = `${Math.min(maxHeight, availableHeight)}px`;

  if (popover.offsetHeight < content.scrollHeight) {
    popover.classList.add("is-scrollable");
    content.setAttribute("tabindex", "0");
  } else {
    popover.classList.remove("is-scrollable");
    content.removeAttribute("tabindex");
  }

  // Horizontal positioning
  const contentWidth = content.offsetWidth;
  popover.style.left = `${
    -getHorizontalCenter(button) * contentWidth +
    (parseFloat(getComputedStyle(button).marginLeft) || 0) +
    button.offsetWidth / 2
  }px`;
  wrapper.style.maxWidth = `${contentWidth}px`;

  const tooltip = popover.querySelector(
    ".footnote-popover__tooltip"
  ) as HTMLElement;
  if (tooltip) tooltip.style.left = `${100 * getHorizontalCenter(button)}%`;

  return position;
}

function setup() {
  const links = findFootnoteLinks();
  const footnotes: Footnote[] = [];
  const positions = new Map<string, string>();
  let activeId: string | null = null;

  for (let i = 0; i < links.length; i++) {
    const fn = createFootnote(links[i], i);
    if (fn) {
      footnotes.push(fn);
      positions.set(fn.id, "above");
    }
  }

  if (footnotes.length === 0) return;

  function findById(id: string): Footnote | undefined {
    return footnotes.find((f) => f.id === id);
  }

  function activate(footnote: Footnote) {
    footnote.button.setAttribute("aria-expanded", "true");
    footnote.button.classList.add("is-active");
    footnote.button.insertAdjacentElement("afterend", footnote.popover);
    footnote.popover.style.maxWidth = `${document.body.clientWidth}px`;

    const pos = positionPopover(footnote, positions.get(footnote.id)!);
    positions.set(footnote.id, pos);

    requestAnimationFrame(() => footnote.popover.classList.add("is-active"));
    activeId = footnote.id;
  }

  function dismiss(footnote: Footnote) {
    footnote.button.setAttribute("aria-expanded", "false");
    footnote.button.classList.remove("is-active");
    footnote.popover.classList.remove("is-active");

    setTimeout(() => footnote.popover.remove(), 250);
    if (activeId === footnote.id) activeId = null;
  }

  function dismissAll() {
    for (const fn of footnotes) {
      if (fn.button.classList.contains("is-active")) dismiss(fn);
    }
  }

  function toggle(id: string) {
    const fn = findById(id);
    if (!fn) return;

    if (fn.button.classList.contains("is-active")) {
      dismiss(fn);
    } else {
      dismissAll();
      activate(fn);
    }
  }

  const repositionAll = throttle(() => {
    for (const fn of footnotes) {
      if (document.body.contains(fn.popover)) {
        const pos = positionPopover(fn, positions.get(fn.id)!);
        positions.set(fn.id, pos);
      }
    }
  }, 16);

  const resizeAll = throttle(() => {
    for (const fn of footnotes) {
      if (document.body.contains(fn.popover)) {
        const pos = positionPopover(fn, positions.get(fn.id)!);
        positions.set(fn.id, pos);
      }
    }
  }, 16);

  const controller = new AbortController();
  const opts = { signal: controller.signal };

  document.addEventListener(
    "click",
    (e) => {
      const button = (e.target as HTMLElement).closest(".footnote-button");
      if (button) {
        e.preventDefault();
        const host = button.closest(".footnote-host");
        const fn = footnotes.find((f) => f.host === host);
        if (fn) toggle(fn.id);
        return;
      }

      if (!(e.target as HTMLElement).closest(".footnote-popover")) {
        dismissAll();
      }
    },
    opts
  );

  document.addEventListener(
    "keyup",
    (e) => {
      if (e.key === "Escape") dismissAll();
    },
    opts
  );

  window.addEventListener("scroll", repositionAll, opts);
  window.addEventListener("resize", resizeAll, opts);

  // Scroll indicator on popover content
  for (const fn of footnotes) {
    fn.content.addEventListener(
      "wheel",
      throttle((e: WheelEvent) => {
        const el = e.currentTarget as HTMLElement;
        const delta = -e.deltaY;
        if (delta > 0) {
          fn.popover.classList.remove("is-fully-scrolled");
        } else if (
          el &&
          delta <= 0 &&
          delta < el.clientHeight + el.scrollTop - el.scrollHeight
        ) {
          fn.popover.classList.add("is-fully-scrolled");
        }
      }, 16),
      opts
    );
  }
}

if (
  document.readyState === "complete" ||
  document.readyState === "interactive"
) {
  setup();
}
</script>

<style>
.footnote-host {
  position: relative;
}

.footnote-button {
  background-color: #949494;
  border: 0;
  border-radius: 0.5rem;
  color: #fff;
  cursor: pointer;
  display: inline-block;
  font-size: 0.75rem;
  height: 1rem;
  margin: 0 0.1rem;
  padding: 0 0.6rem;
  text-decoration: none;
  transition: background-color 0.25s ease, color 0.25s ease;
  vertical-align: baseline;
  max-width: max-content;
  z-index: 0;
  margin-left: calc(var(--space) / 2);
  margin-top: calc(var(--space) / 2);
}

.footnote-button.is-active,
.footnote-button:active,
.footnote-button:focus,
.footnote-button:hover {
  background-color: #3f3f3f;
  color: #fff;
}

.footnote-button svg {
  float: left;
  height: 0.3rem;
}

.footnote-popover {
  --tooltip-size: 0.5rem;

  border: 1px solid var(--color-background-offset, #949494);
  border-radius: 0.5rem;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
  margin: calc(var(--tooltip-size) + 1rem) 0;
  max-width: 90%;
  position: absolute;
  top: 0;
  transform: scale(0.1) translateZ(0);
  transform-origin: 50% 0;
  transition: opacity 0.25s ease, transform 0.25s ease;
  width: 22em;
  z-index: 1;
  background-color: var(--color-background-card, #f5f5f5);
}

.footnote-popover.is-above {
  bottom: 0;
  top: auto;
}

.footnote-popover.is-active {
  transform: scale(1) translateZ(0);
}

.footnote-popover__wrapper {
  position: relative;
  z-index: 1;
  border-radius: 0.5rem;
}

.footnote-popover__content {
  -webkit-overflow-scrolling: touch;
  background-color: var(--color-background-card, #f5f5f5);
  border-radius: 0.5rem;
  box-sizing: border-box;
  color: var(--color-foreground-offset, #111);
  max-height: 15em;
  overflow: auto;
  padding: calc(var(--space, 0.6rem));
  max-width: var(--width-content, none);
  width: 100%;
  word-break: break-word;
}

.footnote-popover__content img {
  max-width: 100%;
}

.footnote-popover__content:focus {
  outline: none;
}

.footnote-popover__content p:last-child {
  margin-bottom: 0;
}

.is-scrollable .footnote-popover__content::after {
  bottom: 0;
  color: #3f3f3f;
  content: "\21E3";
  display: block;
  left: 0;
  opacity: 1;
  position: sticky;
  text-align: center;
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.is-scrollable.is-fully-scrolled .footnote-popover__content::after {
  opacity: 0;
}

.footnote-popover__tooltip {
  background-color: var(--color-background-card, #f5f5f5);
  border: 1px solid var(--color-background-offset, #949494);
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
  height: calc(var(--tooltip-size) * 2);
  margin-left: calc(var(--tooltip-size) * -1);
  position: absolute;
  transform: rotate(45deg);
  width: calc(var(--tooltip-size) * 2);
}

.is-below .footnote-popover__tooltip {
  top: calc(var(--tooltip-size) * -1);
}

.is-above .footnote-popover__tooltip {
  bottom: calc(var(--tooltip-size) * -1);
}

@media (max-width: 767.98px) {
  .footnote-popover {
    border-radius: 0;
    border-width: 1px 0 0;
    bottom: 0 !important;
    left: 0 !important;
    margin: 0;
    max-width: 100% !important;
    position: fixed;
    right: 0 !important;
    top: auto !important;
    transform: translateY(100%);
    width: 100%;
  }

  .footnote-popover.is-active {
    transform: translateY(0);
  }

  .footnote-popover__wrapper {
    border-radius: 0;
    max-width: 100% !important;
    transform: none;
  }

  .footnote-popover__tooltip {
    display: none;
  }
}

@media not print {
  .footnote--print-hidden {
    display: none;
  }
}

@media print {
  .footnote-button,
  .footnote-popover {
    display: none;
  }
}
</style>
